import { UserState } from "@/auth/user"
import { Promotion, PromotionStage } from "../types"
import { use_org_users, user_select_options } from "../hooks/use_org_users"
import { SearchableSelectField } from "./form_fields"

interface OwnershipSectionProps {
  promotion: Pick<Promotion, "owner_id" | "approver_id" | "stage">
  onChange: (changes: Pick<Promotion, "owner_id" | "approver_id">) => void
  /** Outline the approver in red when it is required (submitting) and missing. */
  approverError?: boolean
  /** Set when the form sits inside a Dialog. */
  insideModal?: boolean
}

/** No approver chosen yet. */
const NO_APPROVER = ""

/** PURE: the one-line explanation under the approver picker for a promotion's stage. */
export function approver_help(stage: PromotionStage | undefined): string {
  switch (stage) {
    case "Submitted":
      return "Waiting on this person. They approve it from the promo calendar, or send it back to draft."
    case "Approved":
    case "Running":
    case "Completed":
      return "Who approved the plan."
    default:
      return "Who needs to sign off before the deal is committed — an area manager, say. Required to submit; optional for a draft."
  }
}

/**
 * Who owns a plan and who approves it. Owner defaults to the rep creating it; the approver is
 * required to submit and stays on the promotion afterwards so the history shows who signed off
 * (1st Phorm: "There's no approval route or owner").
 */
export function OwnershipSection({ promotion, onChange, approverError = false, insideModal }: OwnershipSectionProps) {
  const current_user = UserState.use()
  const org_users = use_org_users() ?? []
  // Staff switched into a brand are not on its user list, but still own what they create.
  const users = current_user && !org_users.some((user) => user.id === current_user.id) ? [current_user, ...org_users] : org_users
  const options = user_select_options(users, current_user?.id)

  return (
    <div class="space-y-3">
      <h6 class="text-sm font-medium text-gray-700 border-t pt-3">Owner &amp; approval</h6>
      <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <SearchableSelectField
          label="Owner"
          value={promotion.owner_id || ""}
          onChange={(value: string) => onChange({ owner_id: value || undefined, approver_id: promotion.approver_id })}
          options={options}
          placeholder={users.length === 0 ? "Loading people..." : "Select the owner"}
          searchPlaceholder="Search people..."
          emptyMessage="No one by that name."
          help="The rep who answers for this plan. It comes back to them if approval is refused."
          insideModal={insideModal}
        />
        <SearchableSelectField
          label="Approver"
          value={promotion.approver_id || NO_APPROVER}
          onChange={(value: string) => onChange({ owner_id: promotion.owner_id, approver_id: value || undefined })}
          options={[{ value: NO_APPROVER, label: "No approver yet" }, ...options]}
          placeholder={users.length === 0 ? "Loading people..." : "Select who approves"}
          searchPlaceholder="Search people..."
          emptyMessage="No one by that name."
          help={approver_help(promotion.stage)}
          error={approverError}
          id="promotion-approver"
          insideModal={insideModal}
        />
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  describe("approver_help", () => {
    it("explains the approver by stage", () => {
      expect(approver_help("Pending")).toMatch(/Required to submit/)
      expect(approver_help("Submitted")).toMatch(/Waiting/)
      expect(approver_help("Approved")).toMatch(/approved/)
    })
  })
}
